import type {
  AddToDataHubPayload,
  AddToDataHubResponse,
  ChangePasswordPayload,
  CheckAvailableDatasetIsRequestedResponse,
  CheckAvailableDatasetIsRequestQuoteResponse,
  CloudDownloadPayload,
  ContactSalesTeamPayload,
  DataCanvasTask,
  DataHubDatasetsFilters,
  DataServiceProvider,
  DatasetDetail,
  DatasetFileCatalog,
  DatasetFileStructure,
  DatasetFiltersResponse,
  DatasetOwnRequestsPayload,
  DatasetRequestPayload,
  DatasetResponse,
  DmpPaginatedResponse,
  DmpResponse,
  DownloadRequest,
  DownloadRequestProgress,
  EnhanceDatasetPayload,
  FileDetail,
  FileTypesModalities,
  GenerateOtpPayload,
  GenerateOtpResponse,
  GetDatasetsPayload,
  GetFileTypesModalitiesPayload,
  JourneyInitResponse,
  LoginUserPayload,
  LoginUserResponse,
  PasswordResetPayload,
  PersonalDatasetResponse,
  ProfileInfoResponse,
  RecommendedDatasetResponse,
  RefreshTokenResponse,
  RequestQuotePayload,
  RequestQuoteResponse,
  SignupUserPayload,
  SignupUserResponse,
  TenantConfig,
  TestCloudDownloadConnectionPayload,
  ToggleDatasetLikeResponse,
  TransferSsoTokenPayload,
  TransferSsoTokenResponse,
  TrickAnalyticEventsPayload,
  UpdateUserInfoPayload,
  UpdateUserInfoResponse,
  UserDatasetDetail,
  UserDatasetRequestsResponse,
  UserDatasetVersionItem,
  UserInfo,
  UserLatestDatasetVersionDetail,
  ValidateBlobCerdentials,
  VerifyOtpPayload,
  VerifyOtpResponse,
} from './api.types';
import { encodeObjectToBase64 } from './api.utils';
import { baseURL, dataMarketplaceService, refreshTokenService } from './service';

export const loginUser = (payload: LoginUserPayload) => {
  return dataMarketplaceService
    .post<DmpResponse<LoginUserResponse>>('/api/v1/users/login/', payload)
    .then((res) => res.data);
};

export const logOutUser = () => {
  return dataMarketplaceService.post<DmpResponse<unknown>>('/api/v1/users/logout/').then((res) => res.data);
};

export const signupUser = (payload: SignupUserPayload) => {
  return dataMarketplaceService
    .post<DmpResponse<SignupUserResponse>>('/api/v1/users/', payload)
    .then((res) => res.data);
};

export const generateOtp = (payload: GenerateOtpPayload) => {
  return dataMarketplaceService
    .post<DmpResponse<GenerateOtpResponse>>('/api/v1/users/generate-otp/', payload)
    .then((res) => res.data);
};

// Verify OTP for a user and mark as verified in both Auth and DMP DBs.
export const verifyOtp = (payload: VerifyOtpPayload) => {
  return dataMarketplaceService.post<VerifyOtpResponse>('/api/v1/users/verify-otp/', payload).then((res) => res.data);
};

export const fetchUserInfo = (id: string) => {
  return dataMarketplaceService.get<DmpResponse<UserInfo>>(`/api/v1/users/${id}`).then((res) => res.data);
};

export const updateUserInfo = (id: string, payload: UpdateUserInfoPayload) => {
  return dataMarketplaceService.put<UpdateUserInfoResponse>(`/api/v1/users/${id}`, payload).then((res) => res.data);
};

export const getDatasetsFilters = (payload?: { admin?: boolean; landing_page?: boolean }) => {
  return dataMarketplaceService
    .get<DatasetFiltersResponse>(`/api/v1/filters`, { params: payload })
    .then((res) => res.data);
};

export const getDatasets = (payload: GetDatasetsPayload) => {
  return dataMarketplaceService.post<DmpResponse<DatasetResponse>>('/api/v1/datasets', payload).then((res) => res.data);
};

export const createDatasetRequest = (payload: DatasetRequestPayload) => {
  return dataMarketplaceService
    .post<DmpResponse<undefined> & { dataset_request_id: string }>('/api/v1/dataset/new-request/', payload)
    .then((res) => res.data);
};

export const createContactSaleTeamRequest = (payload: ContactSalesTeamPayload) => {
  return dataMarketplaceService.post<ContactSalesTeamPayload>('/api/v1/get-in-touch', payload).then((res) => res.data);
};

export const createEnhanceDatasetRequest = (payload: EnhanceDatasetPayload) => {
  return dataMarketplaceService
    .post<ContactSalesTeamPayload>('/api/v1/dataset/enhance-request/', payload)
    .then((res) => res.data);
};

export const getDatasetDetail = (id: string) => {
  return dataMarketplaceService.get<DmpResponse<DatasetDetail>>(`/api/v1/dataset/${id}`).then((res) => res.data);
};

export const getDatasetFile = (payload: {
  dataset_version_id: string;
  folder_path?: string;
  is_procured?: boolean;
}) => {
  return dataMarketplaceService
    .get<DatasetFileStructure>(`/api/v1/dataset-files`, {
      params: {
        ...payload,
      },
    })
    .then((res) => res.data);
};

export const transferSsoToken = (payload: TransferSsoTokenPayload) => {
  return dataMarketplaceService
    .post<DmpResponse<TransferSsoTokenResponse>>('/api/v1/social-login/', payload)
    .then((res) => res.data);
};

// get personal datahub datasets or own datasets
export const getPersonalDatasets = (payload: {
  userId: string;
  own_data?: boolean;
  industries?: string[];
  providers?: string[];
  cost_types?: string[];
}) => {
  const { userId, own_data, ...rest } = payload;
  return dataMarketplaceService
    .post<PersonalDatasetResponse>(`/api/v1/users/${userId}/datasets${own_data ? '?own_data=true' : ''}`, rest)
    .then((res) => res.data);
};

export const getUserDatasetRequests = (
  id: string,
  payload?: { page?: number; page_size?: number; sort_by?: string; sort_dir?: 'asc' | 'desc' }
) => {
  return dataMarketplaceService
    .get<UserDatasetRequestsResponse>(`/api/v1/user/dataset-requests/${id}`, { params: payload })
    .then((res) => res.data);
};

export const addToDataHub = (
  payload: AddToDataHubPayload,
  isLocalDownloadRequired?: boolean,
  isCloudDownloadRequired?: boolean
) => {
  let url;
  if (isLocalDownloadRequired) {
    url = '/api/v1/users/add-dataset/?local_download=true';
  } else if (isCloudDownloadRequired) {
    url = '/api/v1/users/add-dataset/?cloud_download=true';
  } else {
    url = '/api/v1/users/add-dataset/';
  }

  return dataMarketplaceService.post<AddToDataHubResponse>(url, payload).then((res) => res.data);
};

export const getUserDatasetDetail = (payload: { userId: string; datasetVersionId: string; collection_id?: string }) => {
  const { userId, datasetVersionId, collection_id } = payload;
  return dataMarketplaceService
    .get<
      DmpResponse<UserDatasetDetail>
    >(`/api/v1/users/${userId}/dataset-versions/${datasetVersionId}`, { params: { collection_id } })
    .then((res) => res.data);
};

export const getFileDetails = (payload: { datasetVersionId: string; fileId: string; isOwnData?: boolean }) => {
  const { datasetVersionId, fileId, isOwnData } = payload;
  const ownData = isOwnData ? '?is_own_data=true' : '';
  return dataMarketplaceService
    .get<DmpResponse<FileDetail>>(`/api/v1/file-details/${datasetVersionId}/${fileId}${ownData}`)
    .then((res) => res.data);
};

export const getDataHubFileDetails = (payload: { datasetVersionId: string; fileId: string; isOwnData?: boolean }) => {
  const { datasetVersionId, fileId, isOwnData } = payload;
  const ownData = isOwnData ? '?is_own_data=true' : '';
  return dataMarketplaceService
    .get<DmpResponse<FileDetail>>(`/api/v1/datahub/file-details/${datasetVersionId}/${fileId}${ownData}`)
    .then((res) => res.data);
};

export const getUserLatestDatasetVersionDetail = (userId: string, datasetId: string) => {
  return dataMarketplaceService
    .get<DmpResponse<UserLatestDatasetVersionDetail>>(`/api/v1/users/${userId}/datasets/${datasetId}`)
    .then((res) => res.data);
};

export const getUserDatasetVersions = (userId: string, lite?: boolean) => {
  return dataMarketplaceService
    .get<
      DmpResponse<typeof lite extends true ? UserDatasetVersionItem[] : { version_ids: string[] }>
    >(`/api/v1/users/${userId}/dataset-versions`, { params: lite ? { lite: 1 } : {} })
    .then((res) => res.data);
};

export const getDecodedPreviewSasUrl = <T>(encodedUrl: string) => {
  return dataMarketplaceService
    .get<T>(`/api/v1/dataset-file-preview`, { params: { encoded_preview_sas_url: encodedUrl } })
    .then((res) => res.data);
};

export type ResolvedPreviewSas = {
  sas_url: string;
  previewable: boolean;
};

export const getResolvedPreviewSasUrl = (encodedUrl: string) => {
  return dataMarketplaceService
    .get<ResolvedPreviewSas>(`/api/v1/dataset-file-preview`, {
      params: { encoded_preview_sas_url: encodedUrl, resolve: 1 },
    })
    .then((res) => res.data);
};

export const getDecodedDownloadSasUrl = <T>(encodedUrl: string) => {
  return dataMarketplaceService
    .get<T>(`/api/v1/dataset-file-download`, { params: { encoded_download_sas_url: encodedUrl } })
    .then((res) => res.data);
};

export const getPreviewUrl = (encodedUrl: string) => {
  return `${baseURL}/api/v1/dataset-file-preview?encoded_preview_sas_url=${encodedUrl}`;
};

export const getDownloadUrl = (encodedUrl: string) => {
  return `${baseURL}/api/v1/dataset-file-download?encoded_download_sas_url=${encodedUrl}`;
};

export const getDatasetFilesByType = (payload: {
  dataset_version_id: string;
  folder_path?: string;
  file_type: string;
}) => {
  return dataMarketplaceService
    .get<DatasetFileStructure>(`/api/v1/dataset-file-type-filter`, {
      params: payload,
    })
    .then((res) => res.data);
};

export const updatePassword = (userId: string, payload: ChangePasswordPayload) => {
  return dataMarketplaceService
    .put<DmpResponse<UserDatasetDetail>>(`/api/v1/users/password-change/${userId}`, payload)
    .then((res) => res.data);
};

export const transferCloudDownload = (payload: CloudDownloadPayload) => {
  const encodedPayload = encodeObjectToBase64(payload);
  return dataMarketplaceService
    .post<DmpResponse<{ message: string; request_id: string }>>(`/api/v1/files/initiate-transfer/`, encodedPayload)
    .then((res) => res.data);
};

export const testCloudDownloadConnection = (payload: TestCloudDownloadConnectionPayload, path?: string) => {
  const operation = path ? 'read' : 'write';
  const payloadObj = { ...payload, operation, path };
  const encodedPayload = encodeObjectToBase64(payloadObj);
  return dataMarketplaceService
    .post<
      DmpResponse<{ success: boolean; trace_id: string; error?: string }>
    >(`/api/v1/files/test-connection/`, encodedPayload)
    .then((res) => res.data);
};

/** Uses `refreshTokenService` (no interceptors), not `dataMarketplaceService` — this call's
 * own 401 (an expired/invalid refresh token) must propagate straight through to the caller
 * rather than re-triggering the same "401 -> try to refresh" interceptor it's already part of,
 * which would deadlock awaiting itself. */
export const requestRefreshToken = (refreshToken: string) => {
  return refreshTokenService
    .post<DmpResponse<RefreshTokenResponse>>('/api/v1/users/refresh-token/', { refresh_token: refreshToken })
    .then((res) => res.data);
};

export const passwordReset = (payload: PasswordResetPayload) => {
  return dataMarketplaceService
    .put<DmpResponse<null>>('/api/v1/users/password-reset/', payload)
    .then((res) => res.data);
};

export const downloadDataset = (payload: { dataset_version_id: string; user_id: string }) => {
  return dataMarketplaceService.get(`/api/v1/dataset-download`, {
    params: payload,
    responseType: 'blob',
  });
};

export const getDatasetDownloadUrl = (payload: { dataset_version_id: string; user_id: string }) => {
  return `${baseURL}/api/v1/dataset-download?dataset_version_id=${payload.dataset_version_id}&user_id=${payload.user_id}`;
};

export const getDatasetFilesCatalog = (payload: {
  dataset_version_id: string;
  file_path?: string;
  file_type?: string;
  is_own_data?: boolean;
}) => {
  const { dataset_version_id, ...rest } = payload;

  return dataMarketplaceService
    .get<
      DmpResponse<{ catalog: DatasetFileCatalog[]; total_count: number }>
    >(`/api/v1/files-catalog/${dataset_version_id}`, { params: rest })
    .then((res) => res.data);
};

export const getDataHubDatasetFilesCatalog = (payload: {
  dataset_version_id: string;
  file_path?: string;
  file_type?: string;
  is_own_data?: boolean;
  collection_id?: string;
}) => {
  const { dataset_version_id, ...rest } = payload;

  return dataMarketplaceService
    .get<
      DmpResponse<{ catalog: DatasetFileCatalog[]; total_count: number }>
    >(`/api/v1/datahub/files-catalog/${dataset_version_id}`, { params: rest })
    .then((res) => res.data);
};

export const getDatasetFilesCatalogSummary = (datasetVersionId: string) => {
  return dataMarketplaceService
    .get<
      DmpResponse<{
        dataset_version_id: string;
        summary: Record<string, { total_files: number; total_size: number }>;
      }>
    >(`/api/v1/files-catalog-summary/${datasetVersionId}`)
    .then((res) => res.data);
};

export const getDataHubDatasetFilesCatalogSummary = (payload: { datasetVersionId: string; collection_id?: string }) => {
  const { datasetVersionId, collection_id } = payload;
  return dataMarketplaceService
    .get<
      DmpResponse<{
        dataset_version_id: string;
        summary: Record<string, { total_files: number; total_size: number }>;
      }>
    >(`/api/v1/datahub/files-catalog-summary/${datasetVersionId}`, { params: { collection_id } })
    .then((res) => res.data);
};

export const getUserDownloadRequests = (
  userId: string,
  payload?: { page?: number; page_size?: number; sort_by?: string; sort_dir?: 'asc' | 'desc' }
) => {
  return dataMarketplaceService
    .get<
      DmpPaginatedResponse<DownloadRequest[]>
    >(`/api/v1/files/user-transfer-requests/`, { params: { user_id: userId, ...payload } })
    .then((res) => res.data);
};

export const requestAvailableDataset = (payload: {
  dataset_id: string;
  user_id: string;
  purpose_description: string;
  contact_consent: boolean;
  company_name?: string;
}) => {
  return dataMarketplaceService.post(`/api/v1/dataset/available-on-request/`, payload).then((res) => res.data);
};

export const getDownloadRequestProgress = (requestId: string) => {
  return dataMarketplaceService
    .get<DmpResponse<DownloadRequestProgress>>(`/api/v1/files/transfer-progress/${requestId}/`)
    .then((res) => res.data);
};

export const createOwnDatasetRequest = (payload: DatasetOwnRequestsPayload, user_id: string) => {
  const encodedPayload = encodeObjectToBase64(payload);
  return dataMarketplaceService
    .post<DatasetOwnRequestsPayload>(`/api/v1/users/${user_id}/dataset/upload`, encodedPayload)
    .then((res) => res.data);
};

export const checkAvailableDatasetIsRequested = (payload: { dataset_id: string; user_id: string }) => {
  return dataMarketplaceService
    .get<CheckAvailableDatasetIsRequestedResponse>(`/api/v1/dataset/check-dataset-request/`, { params: payload })
    .then((res) => res.data);
};

export const addToRequestQuote = (payload: RequestQuotePayload) => {
  return dataMarketplaceService
    .post<RequestQuoteResponse>(`/api/v1/dataset/request-quote/`, payload)
    .then((res) => res.data);
};

export const checkRequestQuoteDatasetIsRequested = (payload: { dataset_version_id: string; user_id: string }) => {
  return dataMarketplaceService
    .get<CheckAvailableDatasetIsRequestQuoteResponse>(`/api/v1/dataset/check-request-quote/`, { params: payload })
    .then((res) => res.data);
};

export const getDataHubDatasetFilters = (user_id: string) => {
  return dataMarketplaceService
    .get<DmpResponse<DataHubDatasetsFilters>>(`/api/v1/users/${user_id}/datahub/filters`)
    .then((res) => res.data);
};

export const getProfileInfo = (user_id: string) => {
  return dataMarketplaceService.get<ProfileInfoResponse>(`/api/v1/users/${user_id}`).then((res) => res.data);
};

export const trickAnalyticEvents = (payload: TrickAnalyticEventsPayload) => {
  return dataMarketplaceService.post(`/api/v1/metrics/analytic/events/`, payload);
};

export const toggleDatasetLike = async (id: string, liked: boolean) => {
  const res = await dataMarketplaceService.put<DmpResponse<ToggleDatasetLikeResponse>>(`/api/v1/dataset/${id}/like/`, {
    liked,
  });
  return res.data;
};

export const journeyInit = () => {
  return dataMarketplaceService
    .post<JourneyInitResponse>(`/api/v1/metrics/analytic/journey/init/`)
    .then((res) => res.data);
};

export const deleteOwnDataset = (payload: { userId: string; datasetId: string }) => {
  const { userId, datasetId } = payload;
  return dataMarketplaceService.delete(`/api/v1/users/${userId}/datasets/${datasetId}/`).then((res) => res.data);
};

export const getTenantConfig = () => {
  return dataMarketplaceService.get<DmpResponse<TenantConfig>>('/api/v1/tenants/config/').then((res) => res.data);
};

export const validateBlobCredentials = () => {
  return dataMarketplaceService
    .get<ValidateBlobCerdentials>('/api/v1/tenants/validate-blob-cred/')
    .then((res) => res.data);
};

export const getRecommendedDatasets = (search: string) => {
  return dataMarketplaceService
    .get<DmpResponse<RecommendedDatasetResponse>>(`/api/v1/datasets/search`, { params: { search } })
    .then((res) => res.data);
};

export const requestBecomeAPartner = (payload: DataServiceProvider) => {
  return dataMarketplaceService
    .post<DmpResponse<undefined> & { partner_request_id: string }>('/api/v1/become-a-partner', payload)
    .then((res) => res.data);
};

export const dataServicesOffered = () => {
  return dataMarketplaceService
    .get<DmpResponse<string[]>>('/api/v1/partner/data-services-offered')
    .then((res) => res.data);
};

export const exportDatasets = () => {
  return dataMarketplaceService.get<Blob>(`/api/v1/dataset/export`, {
    responseType: 'arraybuffer',
  });
};

export const getFileTypesModalities = (payload: GetFileTypesModalitiesPayload) => {
  return dataMarketplaceService
    .post<DmpResponse<FileTypesModalities>>(`/api/v1/data-canvas-wrapper/filetypes-modalities`, payload)
    .then((res) => res.data);
};

export const getDataCanvasTaskStatus = (payload: { request_id: string; user_id: string }) => {
  return dataMarketplaceService
    .get<DmpResponse<DataCanvasTask> & { last_updated_on: string }>(`/api/v1/data-canvas-wrapper/project-task-status`, {
      params: { request_id: payload.request_id, user_id: payload.user_id },
    })
    .then((res) => res.data);
};

export {
  getNetworkDegradation,
  getBlobAvailability,
  getStorageAvailability,
  getRightSizedInstancess,
  getApplicationMonitoring,
  getUptimeMonitoring,
  getInstances,
  getFinOpsCostConsumption,
  getFinOpsConsumptionByService,
  getFinOpsRecommendations,
  getFinOpsCostSubscriptionOverview,
  getSecOpsOverallInfraHealth,
  getSecOpsTopThreats,
  getSecOpsVulnerabilityRecommendations,
  getSecOpsRecommendationByImpact,
  getSecOpsSubscriptionSecurityScore,
  getSecOpsVulnerabilityCount,
} from './InternalApp/Telemetry/Telemetry.api';
